// src/hooks/useNotificationToast.ts
import { useCallback, useState } from 'react';
import { notificationsAPI } from '../services/api';

export interface QueuedNotificationToast {
  id: number;
  category: string;
  title: string;
  body: string;
}

interface UseNotificationToastReturn {
  // The toast currently on screen, or null if the queue is empty. Always
  // queue[0] - there is deliberately no "showing" flag separate from this;
  // NotificationToast unmounts/remounts based on whether this is null.
  current: QueuedNotificationToast | null;
  // Adds a new toast to the back of the queue. If one is already showing,
  // this one waits its turn (single-slot FIFO, not stacked).
  enqueue: (toast: QueuedNotificationToast) => void;
  // Dismisses the current toast without marking it read, advancing the
  // queue to the next one (if any). Used by both the auto-dismiss timer
  // and the manual close button.
  dismiss: () => void;
  // Marks the current toast's notification as read on the server, then
  // dismisses it the same way. Fire-and-forget on the API call (mirrors
  // useNotifications' own silent-catch convention) - the inbox/badge will
  // reflect the read state on their own next poll, same as marking read
  // from the inbox itself doesn't live-sync to other mounted instances.
  markAsRead: () => Promise<void>;
}

export function useNotificationToast(): UseNotificationToastReturn {
  const [queue, setQueue] = useState<QueuedNotificationToast[]>([]);

  const current = queue[0] ?? null;

  const enqueue = useCallback((toast: QueuedNotificationToast) => {
    setQueue((prev) => [...prev, toast]);
  }, []);

  const dismiss = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const markAsRead = useCallback(async () => {
    setQueue((prev) => {
      const [head] = prev;
      if (head) {
        notificationsAPI.markRead(head.id).catch(() => {
          // silent - inbox/badge simply won't reflect the read state until
          // their own next poll or user action
        });
      }
      return prev.slice(1);
    });
  }, []);

  return { current, enqueue, dismiss, markAsRead };
}
